"use client";

import { usePointerInteractionMedia } from "@/components/feature/pointer-interaction-media";
import { useIsPreviewActive, usePreviewIntentStore } from "@/components/feature/preview-intent";
import { useCoordinatedIconLoad } from "@/hooks/use-coordinated-icon-load";
import { useIconLoadTier } from "@/hooks/use-icon-load-tier";
import {
  recordIconDiagnosticCapture,
  resolveIconDiagnosticSessionId,
  resolveIconDiagnosticTier,
  shouldCaptureIconDiagnostic,
  shouldSkipIconDiagnosticProbes,
} from "@/lib/icon-diagnostic-capture";
import {
  ICON_LOAD_PLACEHOLDER_SRC,
  completeIconLoadSlot,
  isIconLoadPlaceholderSrc,
} from "@/lib/icon-load-coordinator";
import { cn } from "@/lib/utils";
import { WifiSlash } from "@phosphor-icons/react";
import { usePostHog } from "posthog-js/react";
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";

// Failed icons are recoverable: failures cluster (a dropped HTTP/2 connection takes down many
// images at once), so a short retry usually succeeds. We retry up to twice with backoff.
const MAX_IMAGE_RETRIES = 2;

// Window used to detect "whole-page" vs "isolated" failures. If many thumbnails error within this
// window, the cause is connection-level (network/CDN), not a specific file.
const RECENT_ERROR_WINDOW_MS = 2000;
const recentErrorTimestamps: number[] = [];

function pruneRecentErrors(now: number): void {
  while (
    recentErrorTimestamps.length > 0 &&
    now - recentErrorTimestamps[0] > RECENT_ERROR_WINDOW_MS
  ) {
    recentErrorTimestamps.shift();
  }
}

// Records one failing thumbnail (call once per thumbnail, on its first failure) and returns how many
// distinct thumbnails have failed inside the window.
function registerRecentError(): number {
  const now = Date.now();
  pruneRecentErrors(now);
  recentErrorTimestamps.push(now);
  return recentErrorTimestamps.length;
}

// Reads the current count without recording, so a thumbnail's own retries don't inflate the metric.
function countRecentErrors(): number {
  pruneRecentErrors(Date.now());
  return recentErrorTimestamps.length;
}

type NetworkInformationLike = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

// Network Information API is Chromium-only, which matches where most of our failures occur
// (Chrome / Edge / ChromeOS). Returns an empty object where unavailable (Safari/Firefox/SSR).
function getConnectionInfo(): Record<string, unknown> {
  if (typeof navigator === "undefined") return {};
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  if (!connection) return {};
  return {
    effective_connection_type: connection.effectiveType ?? null,
    downlink: typeof connection.downlink === "number" ? connection.downlink : null,
    rtt: typeof connection.rtt === "number" ? connection.rtt : null,
    save_data: typeof connection.saveData === "boolean" ? connection.saveData : null,
  };
}

// Each retry needs a distinct URL or React won't touch the DOM `src` and the browser won't refetch.
// A query param forces a fresh fetch past any poisoned cache/connection.
function buildRetrySrc(imageUrl: string, attempt: number): string {
  if (attempt <= 0) return imageUrl;
  const separator = imageUrl.includes("?") ? "&" : "?";
  return `${imageUrl}${separator}pmretry=${attempt}`;
}

function appendHoverRetryNonce(src: string, nonce: number): string {
  if (nonce <= 0) return src;
  const separator = src.includes("?") ? "&" : "?";
  return `${src}${separator}pmhover=${nonce}`;
}

function resolveImageEventSrc(element: HTMLImageElement): string {
  return element.currentSrc || element.getAttribute("src") || "";
}

interface GameThumbnailProps {
  previewId: string;
  imageUrl: string;
  previewVideoUrl?: string | null;
  alt: string;
  className?: string;
  imageClassName?: string;
  priority?: boolean;
  sizes?: string;
  fill?: boolean;
  width?: number;
  height?: number;
}

type ImageFailureContext = {
  image: HTMLImageElement | null;
  concurrentFailures: number;
  msSinceVisible: number | null;
};

export function GameThumbnail({
  previewId,
  imageUrl,
  previewVideoUrl,
  alt,
  className = "",
  imageClassName = "",
  priority = false,
  sizes,
  fill = true,
  width,
  height,
}: GameThumbnailProps) {
  // We only hide the image once playback actually starts, to avoid “blank” states and
  // to ensure repeated activate/deactivate cycles still work even if `canplay` doesn’t refire.
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false);
  const [playFailed, setPlayFailed] = useState(false);
  const { prefersReducedMotion } = usePointerInteractionMedia();
  const store = usePreviewIntentStore();
  const isActive = useIsPreviewActive(previewId);
  const posthog = usePostHog();
  const loadRequestId = `${previewId}:${imageUrl}`;
  const { ref: inViewRef, inView, tier: loadTier } = useIconLoadTier();
  const shouldLoad = useCoordinatedIconLoad({
    requestId: loadRequestId,
    imageUrl,
    tier: loadTier,
    priority,
    nearViewport: priority || inView,
  });
  const imageRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isPlayingRef = useRef(false);
  const playTokenRef = useRef(0);
  const diagnosticReasonsRef = useRef<Set<string>>(new Set());

  // Retry state for recovering blank icons (see MAX_IMAGE_RETRIES).
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [hoverRetryNonce, setHoverRetryNonce] = useState(0);
  const retryTimerRef = useRef<number | null>(null);
  const becameVisibleAtRef = useRef<number | null>(null);
  const displaySrc = appendHoverRetryNonce(buildRetrySrc(imageUrl, retryAttempt), hoverRetryNonce);
  const imageSrc = shouldLoad ? displaySrc : ICON_LOAD_PLACEHOLDER_SRC;

  const captureDiagnostic = useCallback(
    (reason: string, extra: Record<string, unknown> = {}, dedupKeySuffix = "") => {
      const key = `${reason}:${imageUrl}:${previewId}${dedupKeySuffix}`;
      if (diagnosticReasonsRef.current.has(key)) return;

      const attempt = typeof extra.attempt === "number" ? extra.attempt : undefined;
      if (
        !shouldCaptureIconDiagnostic({
          reason,
          imageUrl,
          attempt,
          posthog,
        })
      ) {
        return;
      }

      diagnosticReasonsRef.current.add(key);

      const sessionId = resolveIconDiagnosticSessionId(posthog);
      if (reason !== "image_error_final") {
        recordIconDiagnosticCapture(sessionId);
      }

      const payload = {
        reason,
        preview_id: previewId,
        image_url: imageUrl,
        preview_video_url: previewVideoUrl ?? null,
        is_active: isActive,
        in_view: inView,
        priority,
        loading_mode: shouldLoad ? "eager" : "lazy",
        load_tier: loadTier,
        diagnostic_tier: resolveIconDiagnosticTier(posthog),
        path: typeof window !== "undefined" ? window.location.pathname : null,
        ...getConnectionInfo(),
        ...extra,
      };

      posthog?.capture?.("game_thumbnail_diagnostic", payload);

      if (process.env.NODE_ENV !== "production") {
        console.warn("Game thumbnail diagnostic", payload);
      }
    },
    [
      imageUrl,
      inView,
      isActive,
      loadTier,
      posthog,
      previewId,
      previewVideoUrl,
      priority,
      shouldLoad,
    ]
  );

  // Reset retry state when the underlying image changes (e.g. reused component instance). Cancel any
  // pending retry so a stale timer can't append ?pmretry=... to the fresh image that never failed.
  useEffect(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setRetryAttempt(0);
    setLoadFailed(false);
    setHoverRetryNonce(0);
    becameVisibleAtRef.current = null;
  }, [imageUrl]);

  const retryFromHover = useCallback(() => {
    if (!loadFailed) return;
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    const nextHoverNonce = hoverRetryNonce + 1;
    setLoadFailed(false);
    setRetryAttempt(0);
    setHoverRetryNonce(nextHoverNonce);
    becameVisibleAtRef.current = Date.now();
    captureDiagnostic(
      "image_hover_retry",
      { hover_retry_nonce: nextHoverNonce },
      `:hover:${nextHoverNonce}`
    );
  }, [captureDiagnostic, hoverRetryNonce, loadFailed]);

  // Record the first moment the image became visible, to measure time-to-failure.
  useEffect(() => {
    if (inView && becameVisibleAtRef.current === null) {
      becameVisibleAtRef.current = Date.now();
    }
  }, [inView]);

  // Clear any pending retry timer on unmount.
  useEffect(() => {
    return () => {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  const stopVideo = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
        video.currentTime = 0;
      } catch {
        // no-op: defensive
      }
    }
    playTokenRef.current += 1;
    setHasPlaybackStarted(false);
    setPlayFailed(false);
    isPlayingRef.current = false;
  }, []);

  useEffect(() => {
    return store.registerStopHandler(previewId, stopVideo);
  }, [store, previewId, stopVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !previewVideoUrl) return;

    let cancelled = false;
    const resetVideoPlayback = () => {
      try {
        video.pause();
        video.currentTime = 0;
      } catch {
        // no-op
      }
    };
    const isStalePlayResult = (playToken: number) =>
      cancelled || playTokenRef.current !== playToken || store.getActiveId() !== previewId;

    const shouldPlay = isActive && !prefersReducedMotion && !playFailed;
    if (shouldPlay) {
      if (!isPlayingRef.current) {
        const playToken = playTokenRef.current;
        video
          .play()
          .then(() => {
            if (isStalePlayResult(playToken)) return;
            isPlayingRef.current = true;
            setHasPlaybackStarted(true);
          })
          .catch((err) => {
            if (isStalePlayResult(playToken)) return;
            console.warn("Video autoplay failed:", err);
            setPlayFailed(true);
            setHasPlaybackStarted(false);
            resetVideoPlayback();
            isPlayingRef.current = false;
          });
      }
    } else if (isPlayingRef.current) {
      resetVideoPlayback();
      isPlayingRef.current = false;
      playTokenRef.current += 1;
      setHasPlaybackStarted(false);
    }

    return () => {
      cancelled = true;
    };
  }, [prefersReducedMotion, previewVideoUrl, isActive, playFailed, store, previewId]);

  // Cleanup on unmount: clear any pending hover timers and stop video playback
  useEffect(() => {
    return () => {
      stopVideo();
    };
  }, [stopVideo]);

  useEffect(() => {
    if (!isActive) {
      setHasPlaybackStarted(false);
      isPlayingRef.current = false;
      setPlayFailed(false);
    } else {
      setPlayFailed(false);
    }
  }, [isActive]);

  const canShowPreview = !!previewVideoUrl && !prefersReducedMotion;
  const showVideo = canShowPreview && isActive && hasPlaybackStarted && !playFailed;

  useEffect(() => {
    if (shouldSkipIconDiagnosticProbes(posthog)) return;
    if (!shouldLoad || !inView) return;

    const timeoutId = window.setTimeout(() => {
      const image = imageRef.current;
      if (!image) return;
      if (image.complete && image.naturalWidth > 0) return;

      captureDiagnostic("visible_image_still_not_loaded", {
        image_complete: image.complete,
        image_natural_width: image.naturalWidth,
      });
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [captureDiagnostic, inView, posthog, shouldLoad]);

  useEffect(() => {
    if (shouldSkipIconDiagnosticProbes(posthog)) return;
    if (!showVideo) return;

    const timeoutId = window.setTimeout(() => {
      const video = videoRef.current;
      if (!video) return;
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return;

      captureDiagnostic("video_overlay_not_ready", {
        video_ready_state: video.readyState,
        video_network_state: video.networkState,
        image_complete: imageRef.current?.complete ?? null,
        image_natural_width: imageRef.current?.naturalWidth ?? null,
      });
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [captureDiagnostic, posthog, showVideo]);

  // `src` is always present so SSR and native lazy loading keep working. The observer upgrades a
  // pending lazy image to `eager` once it scrolls into view, which forces the browser to load
  // slides that native lazy loading strands inside the transform-based carousel.
  const loading = shouldLoad ? "eager" : "lazy";
  const fetchPriority = priority ? "high" : "auto";

  const scheduleRetryAttempt = useCallback((attempt: number) => {
    const nextAttempt = attempt + 1;
    const backoffMs = 300 * 2 ** attempt;
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
    }
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      setRetryAttempt(nextAttempt);
    }, backoffMs);
  }, []);

  const handleRetryableImageError = useCallback(
    ({ image, concurrentFailures, msSinceVisible }: ImageFailureContext) => {
      captureDiagnostic(
        "image_error",
        {
          image_complete: image?.complete ?? null,
          image_natural_width: image?.naturalWidth ?? null,
          attempt: retryAttempt,
          will_retry: true,
          concurrent_failures: concurrentFailures,
          ms_since_visible: msSinceVisible,
        },
        `:${retryAttempt}`
      );
      scheduleRetryAttempt(retryAttempt);
    },
    [captureDiagnostic, retryAttempt, scheduleRetryAttempt]
  );

  const handleFinalImageError = useCallback(
    ({ image, concurrentFailures, msSinceVisible }: ImageFailureContext) => {
      completeIconLoadSlot(loadRequestId, imageUrl, false);
      setLoadFailed(true);
      captureDiagnostic("image_error_final", {
        image_complete: image?.complete ?? null,
        image_natural_width: image?.naturalWidth ?? null,
        attempts: retryAttempt,
        concurrent_failures: concurrentFailures,
        ms_since_visible: msSinceVisible,
      });
    },
    [captureDiagnostic, imageUrl, loadRequestId, retryAttempt]
  );

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      if (isIconLoadPlaceholderSrc(resolveImageEventSrc(event.currentTarget))) return;

      completeIconLoadSlot(loadRequestId, imageUrl, true);
      setLoadFailed(false);
      // Only meaningful when the load succeeded after at least one retry.
      if (retryAttempt > 0) {
        captureDiagnostic("image_recovered", { attempts: retryAttempt });
      }
    },
    [captureDiagnostic, imageUrl, loadRequestId, retryAttempt]
  );

  const handleImageError = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      if (isIconLoadPlaceholderSrc(resolveImageEventSrc(event.currentTarget))) return;

      const context: ImageFailureContext = {
        image: imageRef.current,
        // Count each thumbnail once (first failure); retries read the count without inflating it.
        concurrentFailures: retryAttempt === 0 ? registerRecentError() : countRecentErrors(),
        msSinceVisible:
          becameVisibleAtRef.current !== null ? Date.now() - becameVisibleAtRef.current : null,
      };

      if (retryAttempt < MAX_IMAGE_RETRIES) {
        handleRetryableImageError(context);
        return;
      }

      handleFinalImageError(context);
    },
    [handleFinalImageError, handleRetryableImageError, retryAttempt]
  );

  return (
    <div
      ref={inViewRef}
      className={cn("relative overflow-hidden w-full h-full", className)}
      data-preview-id={previewId}
    >
      {/* Thumbnail Image */}
      <img
        ref={imageRef}
        src={imageSrc}
        alt={alt}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        loading={loading}
        fetchPriority={fetchPriority}
        sizes={sizes}
        onLoad={handleImageLoad}
        onError={handleImageError}
        className={cn(
          fill && "absolute inset-0 w-full h-full object-cover",
          showVideo && "opacity-0",
          loadFailed && "opacity-0",
          imageClassName
        )}
      />

      {loadFailed && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-neutral-800 cursor-pointer"
          data-testid="game-thumbnail-load-failed"
          aria-hidden="true"
          onMouseEnter={retryFromHover}
        >
          <WifiSlash
            className="w-[30%] h-[30%] min-w-7 min-h-7 max-w-14 max-h-14 text-white/35"
            weight="duotone"
          />
        </div>
      )}

      {/* Preview Video */}
      {canShowPreview && isActive && !playFailed && (
        <video
          ref={videoRef}
          src={previewVideoUrl}
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            showVideo ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          muted
          loop
          playsInline
          preload="metadata"
        />
      )}
    </div>
  );
}
